import {
    Button,
    Checkbox,
    ErrorAlert,
    FormError,
    Notice,
    TextInput,
    linkStyles,
    useTextForm,
} from '@lumlab/platform-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { termsVersion } from '@lumlab/platform-identity/contract';
import { useRegistrationConfig } from '../app/account.ts';
import { authClient } from '../app/auth.ts';
import { AuthScreen, authErrorCode, takeFragmentParameters } from '../app/AuthScreen.tsx';

/**
 * Address confirmation: a link submits its fragment by POST; the code can also be typed.
 * A server GET alone never confirms an address or exposes the secret to access logs.
 */
export function VerifyEmailPage() {
    const location = useLocation();
    const [link, setLink] = useState<{ email: string; code: string; key: number }>();
    useEffect(() => {
        const followLink = () => {
            const parameters = takeFragmentParameters();
            const email = parameters.get('email');
            const code = parameters.get('code');
            if (email && code) setLink((previous) => ({ email, code, key: (previous?.key ?? 0) + 1 }));
        };
        window.addEventListener('hashchange', followLink);
        return () => window.removeEventListener('hashchange', followLink);
    }, []);
    // Pasting a mail link into the already open confirmation tab is a new attempt too.
    return <EmailConfirmation key={link ? `link-${link.key}` : location.key} link={link} />;
}

function EmailConfirmation({ link }: { link?: { email: string; code: string } | undefined }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const config = useRegistrationConfig();
    const [accepted, setAccepted] = useState(false);
    const [needsTerms, setNeedsTerms] = useState(false);
    const location = useLocation();
    const fromSignIn = (location.state as { email?: string } | null)?.email;
    const [initial] = useState(() => {
        const parameters = link ? new URLSearchParams(link) : takeFragmentParameters();
        return { email: parameters.get('email') ?? fromSignIn ?? '', code: parameters.get('code') ?? '' };
    });
    const [error, setError] = useState<{ code: string } | null>(null);
    const [pending, setPending] = useState(false);
    const [resent, setResent] = useState(false);
    const [passwordSetup, setPasswordSetup] = useState(false);
    const autoStarted = useRef(false);
    const form = useTextForm({
        initialValues: initial,
        validate: (values) => ({
            code: /^\d{6}$/.test(values.code.replace(/\s/g, ''))
                ? undefined
                : t('platform.auth.verifyEmail.codeFormat'),
        }),
    });

    const confirm = useCallback(
        async (email: string, code: string, acceptTerms: boolean) => {
            setPending(true);
            setError(null);
            try {
                const result = await authClient.$fetch<{ requiresPasswordSetup: boolean }>('/email-otp/verify-email', {
                    method: 'POST',
                    body: {
                        email: email.trim(),
                        otp: code.replace(/\s/g, ''),
                        ...(acceptTerms && { termsAccepted: true, termsVersion }),
                    },
                });
                if (result.error) {
                    const errorCode = authErrorCode(result.error);
                    if (errorCode === 'confirmation_terms_required') setNeedsTerms(true);
                    setError({ code: errorCode === 'invalid_code' ? 'invalid_confirmation' : errorCode });
                } else if (result.data?.requiresPasswordSetup) setPasswordSetup(true);
                else void navigate('/sign-in', { replace: true, state: { notice: 'emailVerified' } });
            } catch {
                setError({ code: 'internal' });
            } finally {
                setPending(false);
            }
        },
        [navigate],
    );
    const submit = form.onSubmit(({ email, code }) => confirm(email, code, needsTerms && accepted));
    useEffect(() => {
        if (!autoStarted.current && initial.email && /^\d{6}$/.test(initial.code)) {
            autoStarted.current = true;
            void confirm(initial.email, initial.code, false);
        }
    }, [confirm, initial]);

    const resend = async () => {
        const email = form.values.email.trim();
        if (!email) return;
        setError(null);
        try {
            const result = await authClient.emailOtp.sendVerificationOtp({ email, type: 'email-verification' });
            if (result.error) setError({ code: authErrorCode(result.error) });
            else setResent(true);
        } catch {
            setError({ code: 'internal' });
        }
    };

    return (
        <AuthScreen title={t('platform.auth.verifyEmail.title')}>
            {passwordSetup ? (
                <>
                    <Notice tone="success">{t('platform.auth.verifyEmail.passwordSetup')}</Notice>
                    <Link to="/forgot-password" className={linkStyles}>
                        {t('platform.auth.verifyEmail.newPasswordLink')}
                    </Link>
                </>
            ) : (
                <form onSubmit={submit} className="flex flex-col gap-4">
                    <FormError message={form.formError} />
                    {fromSignIn && !resent && <Notice tone="info">{t('platform.auth.verifyEmail.codeSent')}</Notice>}
                    {resent && <Notice tone="info">{t('platform.auth.verifyEmail.resent')}</Notice>}
                    <p className="text-sm text-muted">{t('platform.auth.verifyEmail.intro')}</p>
                    {error && <ErrorAlert error={error} />}
                    {!initial.email && (
                        <TextInput
                            type="email"
                            autoComplete="email"
                            required
                            label={t('platform.auth.email')}
                            {...form.getInputProps('email')}
                        />
                    )}
                    <TextInput
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        maxLength={7}
                        label={t('platform.auth.verifyEmail.code')}
                        {...form.getInputProps('code')}
                    />
                    {needsTerms && config.error && <ErrorAlert error={config.error} />}
                    {needsTerms && config.data && (
                        <Checkbox
                            required
                            checked={accepted}
                            onChange={(event) => setAccepted(event.target.checked)}
                            label={
                                <>
                                    {t('platform.auth.signUp.accept')}{' '}
                                    <a
                                        className={linkStyles}
                                        href={config.data.termsUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {t('platform.auth.signUp.terms')}
                                    </a>
                                </>
                            }
                        />
                    )}
                    <Button type="submit" loading={pending} disabled={needsTerms && (!accepted || !config.data)}>
                        {t('platform.auth.verifyEmail.submit')}
                    </Button>
                    <Button variant="default" onClick={() => void resend()} disabled={!form.values.email.trim()}>
                        {t('platform.auth.verifyEmail.resend')}
                    </Button>
                </form>
            )}
            <Link to="/sign-in" className={`text-sm ${linkStyles}`}>
                {t('platform.auth.backToSignIn')}
            </Link>
        </AuthScreen>
    );
}
